import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
} from '@mui/material';
import type { FC, FormEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditReferenceFieldCommand } from '../../../../core/mapV2/commands/EditReferenceFieldCommand';
import {
    changedCount,
    EditableField,
    FIELD_KINDS,
    initialInput,
    listOptions,
    parseEditInput,
} from '../../../../core/mapV2/edit';
import { formatKilometers } from '../../../../core/mapV2/format';
import { editDialogQuery } from '../../../../core/mapV2/queries';
import { attributeBreakdowns, formatAxes } from '../../../../core/mapV2/selection';
import { editDialogClosed } from '../../../../core/mapV2/state';
import { roadSectionsQuery, selectedRoadSectionsQuery } from '../../../../core/reference/queries';
import { useAppDispatch, useAppSelector } from '../../../../types';

const EditForm: FC<{ field: EditableField }> = ({ field }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const selected = useAppSelector(selectedRoadSectionsQuery);
    const all = useAppSelector(roadSectionsQuery);
    const [input, setInput] = useState(() => initialInput(selected, field));
    const [saving, setSaving] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const kind = FIELD_KINDS[field];
    const label = t(`mapV2.sheet.${field}`);

    const options = useMemo(() => (kind === 'list' ? listOptions(all, field) : []), [all, field, kind]);
    const breakdown = useMemo(() => attributeBreakdowns(selected).find((entry) => entry.field === field)!, [
        selected,
        field,
    ]);
    const parsed = parseEditInput(field, input);
    const unchanged = parsed.ok && changedCount(selected, field, parsed.value) === 0;
    const length = selected.reduce((total, section) => total + Math.abs(section.cumulEnd - section.cumulStart), 0);
    const axes = Array.from(new Set(selected.map((section) => section.roadName)));
    const currentText = breakdown.homogeneous
        ? breakdown.value
        : breakdown.values.map((entry) => `${entry.value} ×${entry.count}`).join(', ');

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!parsed.ok || unchanged || saving) {
            return;
        }
        setSaving(true);
        setFailure(null);
        const result = await dispatch(EditReferenceFieldCommand({ field, value: parsed.value })).unwrap();
        if (result.status === 'invalid') {
            setFailure(t(`reference.error.${result.field}`, { defaultValue: t('reference.error.default') }));
        } else if (result.status === 'error') {
            setFailure(t('mapV2.edit.failure'));
        }
        setSaving(false);
    };

    const helperText = !parsed.ok ? t(`reference.error.${parsed.error}`) : undefined;
    const inputProps = {
        label: t('mapV2.edit.newValue'),
        size: 'small' as const,
        fullWidth: true,
        autoFocus: true,
        error: !parsed.ok,
        helperText,
    };

    return (
        <form onSubmit={submit} data-testid="map-v2-edit-dialog">
            <DialogTitle sx={{ fontSize: 16 }}>
                {t(`mapV2.edit.titles.${field}`)} – {t('mapV2.edit.scope', { count: selected.length })}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 420 }}>
                <TextField
                    label={t('mapV2.edit.current')}
                    value={currentText}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{ mt: 1 }}
                />
                {kind === 'list' ? (
                    <Autocomplete
                        freeSolo
                        options={options}
                        inputValue={input}
                        onInputChange={(_, value) => setInput(value)}
                        renderInput={(params) => <TextField {...params} {...inputProps} />}
                    />
                ) : (
                    <TextField
                        {...inputProps}
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        InputProps={{
                            inputProps: { inputMode: kind === 'year' ? 'numeric' : 'decimal', 'aria-label': label },
                            endAdornment:
                                field === 'width' ? <InputAdornment position="end">m</InputAdornment> : undefined,
                        }}
                    />
                )}
                {parsed.ok && parsed.value === null && !unchanged ? (
                    <Alert severity="warning">{t('mapV2.edit.clearWarning')}</Alert>
                ) : null}
                {parsed.ok && parsed.value !== null && !unchanged && !breakdown.homogeneous ? (
                    <Alert severity="warning">{t('mapV2.edit.mixedWarning', { count: breakdown.distinct })}</Alert>
                ) : null}
                {unchanged ? <Alert severity="info">{t('mapV2.edit.unchanged')}</Alert> : null}
                {failure ? <Alert severity="error">{failure}</Alert> : null}
                <span style={{ fontSize: 12, color: '#64748B' }}>
                    {t('mapV2.edit.recap', {
                        scope: t('mapV2.edit.scope', { count: selected.length }),
                        length: formatKilometers(length),
                        axes: formatAxes(axes),
                    })}
                </span>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => dispatch(editDialogClosed())} disabled={saving}>
                    {t('mapV2.edit.cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!parsed.ok || unchanged || saving}>
                    {saving ? t('mapV2.edit.saving') : t('mapV2.edit.save')}
                </Button>
            </DialogActions>
        </form>
    );
};

// Edit dialog of one referential field for the selected sections (one or several). Opened from the object
// sheet (pencil) and the multi-selection panel (bulk edit buttons).
export const EditReferenceFieldDialog: FC = () => {
    const dispatch = useAppDispatch();
    const field = useAppSelector(editDialogQuery);

    if (field === null) {
        return null;
    }

    return (
        <Dialog open onClose={() => dispatch(editDialogClosed())} maxWidth="sm">
            <EditForm key={field} field={field} />
        </Dialog>
    );
};
